import { useElementBox, useElementRender, type ElementComponent } from './elementRenderContext';
import { boolProp, choiceProp, type ElementProps } from './elementProps';
import { sanitizeElementHtml } from './htmlSanitize';
import './elementAccordion.css';

// A stack of questions drawn as the disclosures they are: a `<details>` per question with the question
// in its `<summary>`, so it opens with a press, an Enter or a Space, and needs no script to do it. A row
// may take its answer from the product the page is about, as a Shopify theme's collapsible "Description"
// tab does: the description keeps its paragraphs and lists, and the row is left out where there is none.

/** How the questions are drawn, exactly as `accordionElement` offers it. */
const LOOKS = ['boxed', 'lines'] as const;

/** Where a row's answer may come from instead of what the merchant typed. */
const ANSWER_SOURCES = ['product.description'] as const;

/** One question and its answer, read out of the `items` list; `source` names a fact the shop answers. */
type Item = { question: string; answer: string; source: (typeof ANSWER_SOURCES)[number] | null };

// Reads the questions, dropping any row that is not one — the schema stores a list open.
function readItems(props: ElementProps): Item[] {
  const raw = props.items;
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((row) => {
    if (typeof row !== 'object' || row === null) return [];
    const { question, answer, source } = row as Record<string, unknown>;
    if (typeof question !== 'string' || question.trim() === '') return [];
    const known = ANSWER_SOURCES.find((value) => value === source) ?? null;
    return [{ question, answer: typeof answer === 'string' ? answer : '', source: known }];
  });
}

// Draws the stack: every question a row, the first one open when the merchant asked for that.
export const AccordionElement: ElementComponent = ({ node }) => {
  const box = useElementBox(node);
  const description = useElementRender().store?.product?.description?.trim() ?? '';
  const items = readItems(node.props).filter((item) => item.source === null || description !== '');
  const firstOpen = boolProp(node.props, 'firstOpen', true);

  return (
    <div {...box} className={[box.className, 'xe-accordion'].filter(Boolean).join(' ')} data-look={choiceProp(node.props, 'look', LOOKS, 'boxed')}>
      {items.map((item, at) => (
        <details key={at} className="xe-accordion-item" open={firstOpen && at === 0 ? true : undefined}>
          <summary className="xe-accordion-question">
            <span className="xe-accordion-words" dir="auto">{item.question}</span>
            <span className="xe-accordion-glyph" aria-hidden="true" />
          </summary>
          {item.source
            ? <div className="xe-accordion-answer xe-accordion-rich" dir="auto" dangerouslySetInnerHTML={{ __html: sanitizeElementHtml(description) }} />
            : <div className="xe-accordion-answer" dir="auto">{item.answer}</div>}
        </details>
      ))}
    </div>
  );
};
